import React from 'react';
import { connect } from 'react-redux';
import _values from 'lodash/values';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import { getApiDataRequest, ApiDataRequest, performApiRequest, ApiDataBinding } from 'react-api-data';
import { Formik } from 'formik';
import { string, object, array } from 'yup';
import { withTranslation, WithTranslation } from 'react-i18next';
import { compose } from 'recompose';

// constants
import { ApiResponse, BgpAttribute, Peering, PeeringAttribute } from 'constants/api';
import { ReduxState } from 'createStore';

// components
import BaseFormModal, { BaseFormModalProps } from './BaseFormModal';
import { BaseFormModalTemplate, FormModalButtons } from './BaseModalTemplate';
import withUserRoles from 'hocs/withUserRoles';
import { NetworkRequestStatus, Roles } from 'constants/literals';
import { PeeringAttributeForm, PeeringAttributesFieldArray } from 'components/utils/PeeringAttributeForm';

interface UpdatePeeringModalProps {
    bgpAttributes: BgpAttribute[];
    rsPeeringAttributes: PeeringAttribute[];
    peeringId: string;
    routerId: string;
    exchange: string;
    toggle: () => void;
    isOpen: boolean;
}

interface UpdatePeeringModalContainerProps {
    request: ApiDataRequest;
    onSubmit: (values: any) => void;
    userRoles: Roles[];
}

type ModalProps = UpdatePeeringModalProps & UpdatePeeringModalContainerProps & BaseFormModalProps & WithTranslation;

export const createRequestBody = (
    propsAttributes: BgpAttribute[],
    formAttributes: Array<{ id?: string; name: string; value: string }>
) => {
    const requestBody = {
        peering: {
            bgp_attributes: cloneDeep(formAttributes),
        },
    };
    propsAttributes.forEach((attribute: BgpAttribute) => {
        const isRemoved = !find(formAttributes, ['id', attribute.id]);
        if (isRemoved) {
            const cloneAttribute: any = cloneDeep(attribute);
            cloneAttribute.delete = true;
            requestBody.peering.bgp_attributes.push(cloneAttribute);
        }
    });
    return requestBody;
};

const peeringModalMapStateToProps = (state: ReduxState, ownProps: UpdatePeeringModalProps) => ({
    request: getApiDataRequest(state.apiData, 'putPeeringAttributes', {
        peeringId: ownProps.peeringId,
        routerId: ownProps.routerId,
        exchange: ownProps.exchange.toUpperCase(),
    }),
});

const peeringModalmapDispatchToProps = (dispatch: any, ownProps: UpdatePeeringModalProps) => {
    return {
        onSubmit: (formValues: any) => {
            const requestBody = createRequestBody(ownProps.bgpAttributes, formValues.bgpAttributes);
            dispatch(
                performApiRequest(
                    'putPeeringAttributes',
                    {
                        peeringId: ownProps.peeringId,
                        routerId: ownProps.routerId,
                        exchange: ownProps.exchange.toUpperCase(),
                    },
                    requestBody
                )
            ).then((response: ApiDataBinding<ApiResponse<Peering>>) => {
                if (response.request.networkStatus === NetworkRequestStatus.Success) {
                    ownProps.toggle();
                }
            });
            return true;
        },
    };
};

class PeeringAttributesUpdateModal extends BaseFormModal<ModalProps> {
    componentDidUpdate(prevProps: Readonly<ModalProps>): void {
        this.handleFormSubmission(prevProps.request, this.props.request, {
            successMessage: this.props.t('notification.peeringAttributesUpdated'),
        });
        if (!this.props.isOpen && prevProps.isOpen && this.formik) {
            this.formik.resetForm({
                bgpAttributes: this.props.bgpAttributes,
                rsPeeringAttributes: this.props.rsPeeringAttributes,
            });
        }
    }

    render() {
        // Notice: It gets isOpen, toggle options from its props, but not from its state, like other modals do
        const { onSubmit, bgpAttributes, toggle, isOpen, t, rsPeeringAttributes } = this.props;
        return (
            <Formik
                data-test="form"
                ref={(formik) => (this.formik = formik)}
                initialValues={{
                    bgpAttributes: bgpAttributes || [],
                    rsPeeringAttributes: rsPeeringAttributes || [],
                }}
                validationSchema={object().shape({
                    bgpAttributes: array().of(
                        object().shape({
                            id: string(),
                            name: string().required(t('validation.nameRequired')),
                            value: string().required(t('validation.valueRequired')),
                        })
                    ),
                })}
                onSubmit={onSubmit}
            >
                {({ values, isSubmitting, handleSubmit, setFieldValue }) => (
                    <BaseFormModalTemplate
                        data-test="peering-attr-modal-tmpl"
                        isOpen={isOpen}
                        toggle={toggle}
                        onClosed={this.handleModalClose}
                        handleSubmit={handleSubmit}
                        header={t('peering.modalHeaderTitle')}
                        body={
                            <>
                                {values.rsPeeringAttributes.map((attribute, index) => (
                                     <PeeringAttributeForm
                                        key={attribute.uuid}
                                        name={`rsPeeringAttributes[${index}]`}
                                        setFieldValue={setFieldValue}
                                        initialAttributeName={attribute.name}
                                        readonly
                                    />
                                ))}
                                <PeeringAttributesFieldArray
                                    name="bgpAttributes"
                                    attributes={values.bgpAttributes}
                                    setFieldValue={setFieldValue}
                                />
                            </>
                        }
                        footer={
                            <FormModalButtons
                                data-test="peering-attr-modal-buttons"
                                loading={isSubmitting}
                                toggle={toggle}
                            />
                        }
                    />
                )}
            </Formik>
        );
    }
}

const PeeringAttributesUpdateModalContainer = compose<ModalProps, UpdatePeeringModalProps>(
    connect(peeringModalMapStateToProps, peeringModalmapDispatchToProps),
    withTranslation(),
    withUserRoles
)(PeeringAttributesUpdateModal);

export default PeeringAttributesUpdateModalContainer;
